import { apiClient } from "@/lib/api";
import type {
  ChatMessage,
  ChatThread,
  ChatThreadContext,
  PaginatedMessages,
} from "@/types/chat";

type SendMessagePayload = {
  body: string;
  image_url?: string;
  reply_to_id?: number;
  /** Idempotency key (AR-5): a repeat with the same key stores nothing new. */
  client_uuid?: string;
};

/**
 * Extracts the `cursor` query-param value from a DRF CursorPagination `next`
 * URL.  The backend returns a full absolute URL such as
 * `http://host/api/v1/chat/threads/5/messages/?cursor=abc123`.
 * We only need the token so we can forward it as `?cursor=<token>`.
 * Returns `null` when the URL is null/invalid or has no cursor param.
 */
function extractCursor(nextUrl: string | null): string | null {
  if (!nextUrl) return null;
  try {
    const url = new URL(nextUrl);
    return url.searchParams.get("cursor");
  } catch {
    return null;
  }
}

/**
 * Fetches all chat threads for the current dispatcher.
 * GET /api/v1/chat/threads/
 */
export async function getThreads(): Promise<ChatThread[]> {
  const response = await apiClient.get<ChatThread[]>("/v1/chat/threads/");
  return response.data;
}

/**
 * The chat side panel for one thread: driver phone, presence, vehicle,
 * warehouse and the active route with next stop and lateness (TASK-5124).
 * GET /api/v1/chat/threads/{threadId}/context/
 */
export async function getThreadContext(
  threadId: number,
): Promise<ChatThreadContext> {
  const response = await apiClient.get<ChatThreadContext>(
    `/v1/chat/threads/${threadId}/context/`,
  );
  return response.data;
}

/**
 * Gets an existing thread with the given driver or creates a new one.
 * POST /api/v1/chat/threads/by-driver/{driverId}/
 */
export async function getOrCreateThread(driverId: number): Promise<ChatThread> {
  const response = await apiClient.post<ChatThread>(
    `/v1/chat/threads/by-driver/${driverId}/`,
  );
  return response.data;
}

/**
 * Fetches a paginated list of messages for a thread.
 * GET /api/v1/chat/threads/{threadId}/messages/
 *
 * @param threadId - The chat thread ID.
 * @param cursorUrl - Optional full `next` URL returned by the previous page.
 *   The cursor token is extracted and forwarded as `?cursor=<token>`.
 */
export async function getMessages(
  threadId: number,
  cursorUrl?: string | null,
): Promise<PaginatedMessages> {
  const cursor = extractCursor(cursorUrl ?? null);
  const response = await apiClient.get<PaginatedMessages>(
    `/v1/chat/threads/${threadId}/messages/`,
    cursor ? { params: { cursor } } : undefined,
  );
  return response.data;
}

/**
 * Sends a message in the specified thread.
 * POST /api/v1/chat/threads/{threadId}/messages/
 */
export async function sendMessage(
  threadId: number,
  payload: SendMessagePayload,
): Promise<ChatMessage> {
  const response = await apiClient.post<ChatMessage>(
    `/v1/chat/threads/${threadId}/messages/`,
    payload,
  );
  return response.data;
}

/**
 * Soft-deletes a single message (own messages only).
 * DELETE /api/v1/chat/threads/{threadId}/messages/{msgId}/
 */
export async function deleteMessage(
  threadId: number,
  msgId: number,
): Promise<void> {
  await apiClient.delete(`/v1/chat/threads/${threadId}/messages/${msgId}/`);
}

/**
 * Forwards a message to a target thread.
 * POST /api/v1/chat/messages/forward/
 */
export async function forwardMessage(
  messageId: number,
  targetThreadId: number,
): Promise<ChatMessage> {
  const response = await apiClient.post<ChatMessage>(
    "/v1/chat/messages/forward/",
    { message_id: messageId, target_thread_id: targetThreadId },
  );
  return response.data;
}

/**
 * Bulk soft-deletes own messages.
 * DELETE /api/v1/chat/messages/bulk-delete/
 */
export async function bulkDeleteMessages(messageIds: number[]): Promise<void> {
  await apiClient.delete("/v1/chat/messages/bulk-delete/", {
    data: { message_ids: messageIds },
  });
}

/**
 * Marks all messages in the thread as read by the dispatcher.
 * POST /api/v1/chat/threads/{threadId}/read/
 */
export async function markAsRead(threadId: number): Promise<void> {
  await apiClient.post(`/v1/chat/threads/${threadId}/read/`);
}

/**
 * Marks a single message as read by the current user.
 * PATCH /api/v1/chat/threads/{threadId}/messages/{msgId}/read/
 */
export async function markMessageRead(
  threadId: number,
  msgId: number,
): Promise<void> {
  await apiClient.patch(`/v1/chat/threads/${threadId}/messages/${msgId}/read/`);
}

/**
 * Returns the total unread message count for the current dispatcher.
 * GET /api/v1/chat/unread-count/
 */
export async function getUnreadCount(): Promise<{ unread_count: number }> {
  const response = await apiClient.get<{ unread_count: number }>(
    "/v1/chat/unread-count/",
  );
  return response.data;
}

/**
 * Uploads a chat image through the backend proxy. The file is POSTed as
 * multipart/form-data to our own origin (CORS under our control); the backend
 * streams it to S3 and returns the public `image_url`. This replaces the old
 * presigned-URL + direct browser→S3 PUT flow, which required the S3 bucket CORS
 * to list every browser origin (branded tenants use dynamic custom domains, so
 * their preflight failed — BUG-376).
 *
 * POST /api/v1/chat/upload/ (multipart, field `file`)
 */
export async function uploadChatImage(
  file: Blob,
  filename: string,
): Promise<{ image_url: string }> {
  const formData = new FormData();
  formData.append("file", file, filename);
  const response = await apiClient.post<{ image_url: string }>(
    "/v1/chat/upload/",
    formData,
  );
  return response.data;
}
